import type { GameDefinition, Player, TeamPreset } from '@/schemas';

/**
 * Standard color palette for player color assignment
 */
// Colors adapted from https://ui.shadcn.com/colors (distinct, vibrant selection)
export const PLAYER_COLOR_PALETTE = [
  '#ef4444', // red-500
  '#f97316', // orange-500
  '#f59e0b', // amber-500
  '#22c55e', // green-500
  '#10b981', // emerald-500
  '#06b6d4', // cyan-500
  '#3b82f6', // blue-500
  '#4f46e5', // indigo-600
  '#a855f7', // purple-500
  '#ec4899', // pink-500
  '#f43f5e', // rose-500
] as const;

/**
 * Standard color palette for team preset color assignment
 * Colors are distinct from player colors to avoid confusion
 */
// Colors adapted from https://ui.shadcn.com/colors (distinct from player palette)
// Removed similar neutral colors (gray/zinc/stone/neutral/slate) and replaced with more distinct colors
export const TEAM_COLOR_PALETTE = [
  '#8b5cf6', // violet-500
  '#d946ef', // fuchsia-500
  '#14b8a6', // teal-500
  '#84cc16', // lime-500
  '#eab308', // yellow-500
  '#0ea5e9', // sky-500
  '#6366f1', // indigo-500 (different from player indigo-600)
  '#06b6d4', // cyan-500 (different shade from player cyan-500)
  '#10b981', // emerald-500 (different shade from player emerald-500)
  '#f59e0b', // amber-500 (different shade from player amber-500)
  '#ec4899', // pink-500 (different shade from player pink-500)
] as const;

/**
 * Assign a color to a player by rotating through the standard color palette.
 * Returns the next available color from the palette that isn't already assigned.
 */
export const assignPlayerColor = (existingPlayers: Player[]): string => {
  const usedColors = new Set(existingPlayers.map((p) => p.color).filter(Boolean));

  // Find first unused color in palette
  for (const color of PLAYER_COLOR_PALETTE) {
    if (!usedColors.has(color)) {
      return color;
    }
  }

  // If all colors are used, cycle back to start
  const index = existingPlayers.length % PLAYER_COLOR_PALETTE.length;
  return PLAYER_COLOR_PALETTE[index];
};

/**
 * Generate a default session name from a game definition.
 * Format: "{Game Name} - {Date}" where Date is formatted (e.g., "Jan 15, 2024")
 */
export const generateDefaultSessionName = (gameDefinition: GameDefinition): string => {
  const date = new Date();
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedDate = dateFormatter.format(date);
  return `${gameDefinition.name} - ${formattedDate}`;
};

/**
 * Assign a color to a team preset by rotating through the team color palette.
 * Returns the next available color from the palette that isn't already assigned.
 */
export const assignTeamColor = (existingPresets: TeamPreset[]): string => {
  const usedColors = new Set(existingPresets.map((p) => p.color).filter(Boolean));

  // Find first unused color in palette
  for (const color of TEAM_COLOR_PALETTE) {
    if (!usedColors.has(color)) {
      return color;
    }
  }

  // If all colors are used, cycle back to start
  const index = existingPresets.length % TEAM_COLOR_PALETTE.length;
  return TEAM_COLOR_PALETTE[index];
};
